import { getAccessToken } from "../slices/session";
import axios from "./axios";

export async function fetchCompany(params) {
  return axios
    .get(`/company`, { params })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function updateCompany(payload, params) {
  return axios.put(`/company/`, { company: payload, params });
}

export async function fetchTripletexEvents() {
  return axios.get("/company/settings/integrations/tripletex/events");
}

export async function fetchPaymentTypes() {
  return axios.get("/company/settings/payment_types");
}

export async function subscribeTripletexEvent(event) {
  return axios.put("/company/settings/integrations/tripletex/subscribe", {
    event,
  });
}
export async function unsubscribeTripletexEvent(event) {
  return axios.put("/company/settings/integrations/tripletex/unsubscribe", {
    event,
  });
}

export async function syncModule(params) {
  return axios.post(`/tripletex/sync`, { sync: params });
}

export async function deleteTripletexSubscriptions() {
  return axios.put(
    `/company/settings/integrations/tripletex/delete_subscriptions`
  );
}

export async function fetchDashboardCompanyStats() {
  return axios.get("/company/stats");
}

export async function syncEventSource(params) {
  const authToken = getAccessToken();
  const baseURL = process.env.REACT_APP_BACKEND_URL;

  const queryParams = new URLSearchParams(params).toString();
  const url = `${baseURL}/tripletex/sync?${queryParams}`;

  return new EventSource(url, {
    headers: {
      withCredentials: true,
      Authorization: `Bearer ${authToken}`,
    },
  });
}

// Phonero API functions
export async function validatePhoneroConnection() {
  return axios.get(
    "/company/settings/integrations/phonero/validate_connection"
  );
}

export async function fetchPhoneroWebhooks() {
  return axios.get("/company/settings/integrations/phonero/fetch_webhooks");
}

export async function createPhoneroWebhook(webhookUrl) {
  return axios.post("/company/settings/integrations/phonero/create_webhook", {
    webhook_url: webhookUrl,
  });
}

export async function deletePhoneroWebhook(webhookId) {
  return axios.delete(
    `/company/settings/integrations/phonero/delete_webhook?webhook_id=${webhookId}`
  );
}

export async function fetchPhoneroCalls(params) {
  return axios.get("/company/settings/integrations/phonero/fetch_calls", {
    params,
  });
}

export async function fetchPhoneroQueues() {
  return axios.get("/company/settings/integrations/phonero/fetch_queues");
}

export async function startPhoneroWebSocket() {
  return axios.post("/company/settings/integrations/phonero/start_websocket");
}

export async function stopPhoneroWebSocket() {
  return axios.post("/company/settings/integrations/phonero/stop_websocket");
}

// Phonero Presence API functions
export async function fetchPhoneroPresenceCodes() {
  return axios.get("/company/settings/integrations/phonero/presence/codes");
}

export async function fetchPhoneroPresenceStates() {
  return axios.get("/company/settings/integrations/phonero/presence/states");
}

export async function fetchPhoneroUserPresenceState() {
  return axios.get(
    "/company/settings/integrations/phonero/presence/user/state"
  );
}

export async function fetchPhoneroUserPresence(personIdOrUsername) {
  return axios.get(
    `/company/settings/integrations/phonero/presence/user/${personIdOrUsername}`
  );
}

export async function updatePhoneroUserPresence(
  personIdOrUsername,
  presenceData
) {
  return axios.put(
    `/company/settings/integrations/phonero/presence/user/${personIdOrUsername}`,
    {
      presence_data: presenceData,
    }
  );
}

// Phonero Persons API functions
export async function fetchPhoneroRoles() {
  return axios.get("/company/settings/integrations/phonero/roles");
}

export async function fetchPhoneroPersons() {
  return axios.get("/company/settings/integrations/phonero/persons");
}

export async function syncPhoneroPersons() {
  return axios.post("/company/settings/integrations/phonero/persons/sync");
}

export async function updatePhoneroPerson(personId, personData) {
  return axios.patch(
    `/company/settings/integrations/phonero/person/${personId}`,
    {
      person_data: personData,
    }
  );
}

// Phonero Call Operations API functions
export async function startPhoneroClickToDial(callData) {
  return axios.post(
    "/company/settings/integrations/phonero/call/click_to_dial",
    {
      call_data: callData,
    }
  );
}

export async function createPhoneroManualCall(callData) {
  const response = await axios.post(
    "/company/settings/integrations/phonero/call/manual",
    callData
  );
  return response.data;
}

export async function fetchPhoneroStats() {
  return axios.get("/company/settings/integrations/phonero/fetch_stats");
}

export async function refreshPhoneroStats(periodType) {
  return axios.post("/company/settings/integrations/phonero/refresh_stats", {
    period_type: periodType,
  });
}

export async function initializePhoneroStats() {
  return axios.post("/company/settings/integrations/phonero/initialize_stats");
}

export async function clearPhoneroStats() {
  return axios.delete("/company/settings/integrations/phonero/clear_stats");
}

export async function getPhoneroStatsStatus() {
  return axios.get("/company/settings/integrations/phonero/stats_status");
}

export async function syncPhoneroCalls(limit = 50) {
  return axios.post("/company/settings/integrations/phonero/sync_calls", {
    limit,
  });
}

export async function fetchPhoneroLiveCalls(limit = 10) {
  return axios.get("/company/settings/integrations/phonero/fetch_live_calls", {
    params: { limit },
  });
}

export async function startPhoneroSyncScheduler() {
  return axios.post(
    "/company/settings/integrations/phonero/start_sync_scheduler"
  );
}

export async function stopPhoneroSyncScheduler() {
  return axios.post(
    "/company/settings/integrations/phonero/stop_sync_scheduler"
  );
}

export async function getPhoneroSyncSchedulerStatus() {
  return axios.get(
    "/company/settings/integrations/phonero/sync_scheduler_status"
  );
}

export async function getPhoneroStoredCalls(params = {}) {
  // Build query parameters
  const queryParams = {
    limit: params.limit || 50,
    offset: params.offset || 0,
    period: params.period,
    query: params.query,
    status: params.status,
    agent: params.agent,
    queue: params.queue,
    start_date: params.start_date,
    end_date: params.end_date,
    sort: params.sort || "desc",
    sort_by: params.sort_by || "started_at",
  };

  // Remove undefined values
  Object.keys(queryParams).forEach((key) => {
    if (queryParams[key] === undefined || queryParams[key] === null) {
      delete queryParams[key];
    }
  });

  return axios.get("/company/settings/integrations/phonero/stored_calls", {
    params: queryParams,
  });
}

export async function syncPhoneroCommunications(limit = 50) {
  return axios.post(
    "/company/settings/integrations/phonero/sync_communications",
    {
      limit,
    }
  );
}

// Phonero Stats Sync Scheduler API functions
export async function startPhoneroStatsSyncScheduler() {
  return axios.post(
    "/company/settings/integrations/phonero/start_stats_sync_scheduler"
  );
}

export async function stopPhoneroStatsSyncScheduler() {
  return axios.post(
    "/company/settings/integrations/phonero/stop_stats_sync_scheduler"
  );
}

export async function getPhoneroStatsSyncSchedulerStatus() {
  return axios.get(
    "/company/settings/integrations/phonero/stats_sync_scheduler_status"
  );
}

export async function processPhoneroCall(
  callId,
  status = "processed",
  reason = "No action required"
) {
  return axios
    .post("/company/settings/integrations/phonero/process", {
      call_id: callId,
      status: status,
      reason: reason,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

// Initialize HSE modules for company
export const initializeHseModules = async () => {
  const response = await axios.post("/companies/initialize_hse_modules");
  return response;
};

// Company Features API functions
export async function fetchCompanyAiUsageSummary(params = {}) {
  return axios
    .get("/company/ai_usage_summary", { params })
    .then((response) => ({ data: response.data.data, error: false }))
    .catch((error) => ({
      error: true,
      data: error.response?.data,
      status: error.response?.status,
    }));
}

export async function getCompanyFeatures() {
  return axios
    .get("/company/features")
    .then((response) => {
      return { data: response.data.data, error: false };
    })
    .catch((error) => {
      return { error: true, data: error.response?.data };
    });
}

export async function updateCompanyFeatures(payload) {
  return axios
    .put("/company/features", {
      company_feature: payload,
    })
    .then((response) => {
      return { data: response.data.data, error: false };
    })
    .catch((error) => {
      return { error: true, data: error.response?.data };
    });
}

export async function fetchAccountingSummary(year = null) {
  const params = { include_monthly: true };
  if (year) {
    params.year = year;
  }
  return axios
    .get("/company/accounting/summary", { params })
    .then((response) => {
      return { data: response.data.data, error: false };
    })
    .catch((error) => {
      return { error: true, data: error.response?.data };
    });
}